import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, FileText, Loader2, ImageIcon } from "lucide-react";

const SUBJECTS = ["English", "Kannada", "Mathematics", "Science", "Social Studies"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * TeacherAnswerSheetDialog — teachers upload / manage a student's answer sheets
 * (images or PDF) per subject for a chosen exam. Students later view these on
 * the leaderboard to learn how to write good answers.
 */
export default function TeacherAnswerSheetDialog({ open, onClose, student, examOptions = [], defaultExamKey }) {
  const [subject, setSubject] = useState("English");
  const [examKey, setExamKey] = useState(defaultExamKey || examOptions[0]?.key || "");
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSubject("English");
      setExamKey(defaultExamKey || examOptions[0]?.key || "");
    }
    // eslint-disable-next-line
  }, [open, student?.student_id]);

  const load = async () => {
    if (!student?.student_id || !examKey || !subject) { setSheets([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/answer-sheets", { params: { student_id: student.student_id, exam_key: examKey, subject } });
      setSheets(data.items || []);
    } catch { setSheets([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, subject, examKey, student?.student_id]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!examKey) { toast.error("Pick an exam first."); return; }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("student_id", student.student_id);
        fd.append("subject", subject);
        fd.append("exam_key", examKey);
        fd.append("file", f);
        await api.post("/answer-sheets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${files.length} file${files.length > 1 ? "s" : ""}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/answer-sheets/${id}`); setSheets((s) => s.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  const fileUrl = (id) => `${BACKEND}/api/answer-sheets/file/${id}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="teacher-answersheet-dialog">
        <DialogHeader>
          <DialogTitle>Answer sheets · {student?.name}</DialogTitle>
          <DialogDescription>Upload this student's answer sheets so classmates can learn from them.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-500 mb-1">Exam</label>
            <Select value={examKey} onValueChange={setExamKey}>
              <SelectTrigger className="h-10 rounded-xl" data-testid="as-exam-select"><SelectValue placeholder="Select exam" /></SelectTrigger>
              <SelectContent>{examOptions.map((c) => <SelectItem key={c.key} value={c.key}>{c.label || c.key}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-500 mb-1">Subject</label>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger className="h-10 rounded-xl" data-testid="as-subject-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={onPick} data-testid="as-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading || !examKey} className="w-full rounded-xl bg-sky-500 hover:bg-sky-600" data-testid="as-upload-btn">
          {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4 mr-1" /> Upload images / PDF</>}
        </Button>

        <div className="max-h-64 overflow-y-auto space-y-2 mt-1" data-testid="as-list">
          {loading ? (
            <div className="text-center text-sm text-slate-400 py-6">Loading…</div>
          ) : sheets.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-6" data-testid="as-empty">No sheets yet for {subject}.</div>
          ) : (
            sheets.map((sh) => {
              const isPdf = (sh.mime || "").includes("pdf");
              return (
                <div key={sh.id} className="flex items-center gap-3 p-2 rounded-xl border border-slate-200 dark:border-slate-700" data-testid={`as-item-${sh.id}`}>
                  {isPdf ? <FileText className="h-8 w-8 text-rose-500 shrink-0" /> : (
                    <img src={fileUrl(sh.id)} alt="" className="h-10 w-10 rounded object-cover shrink-0 bg-slate-100" />
                  )}
                  <a href={fileUrl(sh.id)} target="_blank" rel="noreferrer" className="flex-1 min-w-0 text-sm text-sky-600 hover:underline truncate">{sh.name}</a>
                  <button onClick={() => remove(sh.id)} className="text-slate-400 hover:text-rose-500" data-testid={`as-delete-${sh.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
